import { View, Text, styled, ScrollView } from 'tamagui'
import { useState } from 'react'
import { useAppStore } from '@/store'
import { useToastStore } from '@/store/toastStore'
import { users } from '@/mocks'
import { roles, memberRoles } from '@/mocks/roles'
import { Avatar } from '../../common'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 4, maxWidth: 700 })

const MemberRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', height: 52, paddingHorizontal: 12,
  borderRadius: '$borderRadiusLg', gap: 12,
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const MemberInfo = styled(View, { flex: 1, gap: 2 })

const MemberName = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, fontWeight: '500', color: '$surfaceForeground',
})

const MemberTag = styled(Text, {
  fontFamily: '$fontMono', fontSize: 11, color: '$color8',
})

const RoleBadge = styled(View, {
  paddingHorizontal: 8, paddingVertical: 3, borderRadius: '$borderRadiusSm',
})

const RoleText = styled(Text, {
  fontFamily: '$fontBody', fontSize: 11, fontWeight: '600',
})

const ActionButton = styled(View, {
  paddingHorizontal: 10, paddingVertical: 4, borderRadius: '$borderRadiusSm',
  cursor: 'pointer', borderWidth: 1, borderColor: '$borderDefault',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const ConfirmOverlay = styled(View, {
  position: 'absolute',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: '$overlay',
  alignItems: 'center', justifyContent: 'center',
  zIndex: 3000,
})

const ConfirmModal = styled(View, {
  width: 360,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 20,
  gap: 12,
})

const ConfirmTitle = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 18, color: '$surfaceForeground',
})

const ConfirmDesc = styled(Text, {
  fontFamily: '$fontBody', fontSize: 13, color: '$sidebarMuted', lineHeight: 18,
})

const ConfirmActions = styled(View, {
  flexDirection: 'row', gap: 8, justifyContent: 'flex-end', marginTop: 4,
})

const ConfirmBtn = styled(View, {
  paddingHorizontal: 16, paddingVertical: 8, borderRadius: '$borderRadiusMd',
  cursor: 'pointer',
  // @ts-expect-error -- web transition
  style: { transition: 'opacity 100ms ease' },
})

export function MemberSettings() {
  const { activeServerId } = useAppStore()
  const addToast = useToastStore((s) => s.addToast)
  const [confirmAction, setConfirmAction] = useState<{ type: 'kick' | 'ban'; userId: string } | null>(null)
  const [reason, setReason] = useState('')
  const serverId = activeServerId || ''
  const serverMemberRoles = memberRoles[serverId] || {}
  const serverRoles = roles[serverId] || []

  const memberIds = Object.keys(serverMemberRoles)

  return (
    <Container>
      <Header>
        <Title>Members ({memberIds.length})</Title>
      </Header>
      <ScrollView flex={1}>
        <Body>
          {memberIds.map((userId) => {
            const user = users[userId]
            if (!user) return null
            const roleId = serverMemberRoles[userId]
            const role = serverRoles.find(r => r.id === roleId)
            return (
              <MemberRow key={userId}>
                <Avatar name={user.displayName} color={user.roleColor || '#8B95A8'} status={user.status} size="md" />
                <MemberInfo>
                  <MemberName color={role?.color || '$surfaceForeground'}>{user.displayName}</MemberName>
                  <MemberTag>@{user.username}#{user.discriminator}</MemberTag>
                </MemberInfo>
                {role && (
                  <RoleBadge backgroundColor={`${role.color}22`}>
                    <RoleText color={role.color}>{role.name}</RoleText>
                  </RoleBadge>
                )}
                <ActionButton onPress={() => setConfirmAction({ type: 'kick', userId })}>
                  <Text fontFamily="$fontBody" fontSize={11} color="$sidebarMuted">Kick</Text>
                </ActionButton>
                <ActionButton
                  borderColor="$error"
                  onPress={() => setConfirmAction({ type: 'ban', userId })}
                >
                  <Text fontFamily="$fontBody" fontSize={11} color="$error">Ban</Text>
                </ActionButton>
              </MemberRow>
            )
          })}
        </Body>
      </ScrollView>

      {confirmAction && (
        <ConfirmOverlay onPress={() => setConfirmAction(null)}>
          <ConfirmModal onPress={(e: { stopPropagation: () => void }) => e.stopPropagation()}>
            <ConfirmTitle>
              {confirmAction.type === 'ban' ? 'Ban' : 'Kick'} {users[confirmAction.userId]?.displayName}?
            </ConfirmTitle>
            <ConfirmDesc>
              {confirmAction.type === 'ban'
                ? 'This user will be permanently banned from the server.'
                : 'This user will be removed but can rejoin with an invite.'}
            </ConfirmDesc>
            <View gap={4}>
              <Text fontFamily="$fontBody" fontSize={11} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={0.5}>
                Reason (optional)
              </Text>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter a reason..."
                rows={2}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  color: '#C5CBD9',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 6,
                  outline: 'none',
                  resize: 'none',
                  width: '100%',
                  padding: '6px 8px',
                }}
              />
            </View>
            <ConfirmActions>
              <ConfirmBtn
                onPress={() => { setConfirmAction(null); setReason('') }}
                hoverStyle={{ opacity: 0.8 }}
              >
                <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color="$sidebarMuted">Cancel</Text>
              </ConfirmBtn>
              <ConfirmBtn
                backgroundColor={confirmAction.type === 'ban' ? '$error' : '$warning'}
                onPress={() => {
                  addToast('success', `${users[confirmAction.userId]?.displayName} has been ${confirmAction.type === 'ban' ? 'banned' : 'kicked'}`)
                  setConfirmAction(null)
                  setReason('')
                }}
                hoverStyle={{ opacity: 0.9 }}
              >
                <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color="$surfaceForeground">
                  {confirmAction.type === 'ban' ? 'Ban' : 'Kick'}
                </Text>
              </ConfirmBtn>
            </ConfirmActions>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </Container>
  )
}
